import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import {
  CALLABLE_NAMES,
  type CreateBillingPortalSessionRequest,
  type CreateBillingPortalSessionResponse,
  type CreateCheckoutSessionRequest,
  type CreateCheckoutSessionResponse,
  type PlanTier,
  type SyncSubscriptionRequest,
  type SyncSubscriptionResponse,
} from '@acs/shared';
import { functions } from '../../shared/api/firebase.js';
import { withoutUndefined } from '../../shared/api/billing.js';

const createCheckoutSessionFn = httpsCallable<
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse
>(functions, CALLABLE_NAMES.createCheckoutSession);

const createBillingPortalSessionFn = httpsCallable<
  CreateBillingPortalSessionRequest,
  CreateBillingPortalSessionResponse
>(functions, CALLABLE_NAMES.createBillingPortalSession);

const syncSubscriptionFn = httpsCallable<SyncSubscriptionRequest, SyncSubscriptionResponse>(
  functions,
  CALLABLE_NAMES.syncSubscription,
);

/**
 * Start Checkout and leave for Stripe (doc 23).
 *
 * `window.location.assign`, not a new tab: Checkout is a full payment flow that
 * ends by redirecting back to us, and a popup that a browser blocks — or that
 * the owner closes on their phone — strands them halfway through paying with
 * no way back.
 *
 * `returnOrigin` is the browser's own origin, checked server-side against a
 * published allowlist. Sent rather than hard-coded because dev, preview
 * channels and production all differ, and hard-coding one of them means the
 * other two silently return to the wrong place.
 */
export function useStartCheckout(businessId: string) {
  return useMutation({
    /** `plan`: legacy tiers. `packageId`: prepaid — a minute package billed
     *  together with the subscription's first payment. */
    mutationFn: async (opts: { plan?: PlanTier; packageId?: string | null } = {}) => {
      // Never `plan: undefined`: the callable SDK sends it as null, which the
      // request schema rejected — every prepaid Subscribe click failed with
      // INTERNAL (13 Oct 2026).
      const { data } = await createCheckoutSessionFn(
        withoutUndefined({ businessId, plan: opts.plan, packageId: opts.packageId ?? undefined, returnOrigin: window.location.origin }),
      );
      window.location.assign(data.url);
    },
  });
}

/** Open Stripe's billing portal — cards, invoices, plan changes, cancellation.
 *  Same full-page navigation, for the same reason. */
export function useOpenBillingPortal(businessId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await createBillingPortalSessionFn({
        businessId,
        returnOrigin: window.location.origin,
      });
      window.location.assign(data.url);
    },
  });
}

/**
 * Pull the subscription from Stripe after Checkout returns.
 *
 * The business document is a live Firestore listener, so the success screen
 * will light up on its own the moment the webhook lands — this exists for the
 * seconds before that, and for the deployment where the webhook is not wired
 * up yet. It writes through the same code path the webhook uses, so the two
 * cannot produce different answers.
 *
 * `sessionId` is the `session_id` Stripe puts in the success URL, and passing
 * it through is what makes the "seconds before that" case actually work: the
 * server's fallback lookup goes via Stripe's search index, which lags by up to
 * a minute and therefore never has the subscription this screen is asking
 * about. Undefined elsewhere (Settings), where the lag does not matter.
 */
export function useSyncSubscription(businessId: string, sessionId?: string) {
  return useMutation({
    mutationFn: async () => (await syncSubscriptionFn(withoutUndefined({ businessId, sessionId }))).data,
  });
}
